import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export function formatAvailabilityPct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return `${Number(value).toFixed(Number(value) % 1 === 0 ? 0 : 1)}%`;
}

export function isOpenAlert(status: string | null | undefined): boolean {
  if (!status) return true;
  const s = status.toLowerCase();
  return s !== 'cleared' && s !== 'resolved';
}

export function useCustomerNetworkLive(customerId: string | undefined) {
  return useQuery({
    queryKey: ['admin-customer-network', customerId],
    enabled: Boolean(customerId),
    queryFn: async () => {
      if (!customerId) {
        return { devices: [], alerts: [], interfaces: [], wlans: [], clients: [], accessPoints: [], unifiSites: [] };
      }
      const [devices, alerts, interfaces, wlans, clients, accessPoints, unifiSites] = await Promise.all([
        supabase
          .from('devices')
          .select('id, name, device_type, status, ip_address, mac_address, firmware_version, vendor_name, make_model, serial_number, last_seen_at, availability_pct, location_id, locations(name)')
          .eq('customer_id', customerId)
          .order('name'),
        supabase
          .from('network_alerts')
          .select('id, severity, status, alert_name, message, detected_on, entity_name, device_id, location_id, devices(name), locations(name)')
          .eq('customer_id', customerId)
          .or('status.is.null,and(status.neq.cleared,status.neq.resolved)')
          .order('detected_on', { ascending: false })
          .limit(25),
        supabase
          .from('device_interfaces')
          .select('id, name, interface_type, operational_status, ip_address, negotiated_speed, device_id, devices(name)')
          .eq('customer_id', customerId)
          .order('name')
          .limit(80),
        supabase
          .from('unifi_wlans')
          .select('id, name, enabled, security, hidden, location_id, locations(name)')
          .eq('customer_id', customerId)
          .order('name'),
        supabase
          .from('unifi_clients')
          .select('id, name, ip_address, mac_address, connection_type, ssid, connected_at, location_id, locations(name)')
          .eq('customer_id', customerId)
          .order('name'),
        supabase
          .from('unifi_access_points')
          .select('id, name, model, mac_address, ip_address, firmware_version, device_id, location_id, locations(name)')
          .eq('customer_id', customerId)
          .order('name'),
        supabase
          .from('unifi_site_map')
          .select('id, site_name, unifi_host_name, wifi_client_count, wired_client_count, ssid_count, location_id, locations(name)')
          .eq('customer_id', customerId)
          .order('site_name'),
      ]);
      if (devices.error) throw devices.error;
      if (alerts.error) throw alerts.error;
      if (interfaces.error) throw interfaces.error;
      if (wlans.error) throw wlans.error;
      if (clients.error) throw clients.error;
      if (accessPoints.error) throw accessPoints.error;
      if (unifiSites.error) throw unifiSites.error;
      return {
        devices: devices.data ?? [],
        alerts: alerts.data ?? [],
        interfaces: interfaces.data ?? [],
        wlans: wlans.data ?? [],
        clients: clients.data ?? [],
        accessPoints: accessPoints.data ?? [],
        unifiSites: unifiSites.data ?? [],
      };
    },
    staleTime: 60 * 1000,
  });
}

export function useOpenAlertCount() {
  return useQuery({
    queryKey: ['admin-open-alert-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('network_alerts')
        .select('id', { count: 'exact', head: true })
        .or('status.is.null,and(status.neq.cleared,status.neq.resolved)');
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 30 * 1000,
  });
}
